import { reportError } from './ErrorReporter';

const KNOWN_GOOGLE_SCRIPT_HOSTS = [
  'google.com',
  'googleapis.com',
  'googleadservices.com',
  'googlesyndication.com',
  'googletagmanager.com',
  'gstatic.com',
  'doubleclick.net',
];
type ScriptAttribution =
  'first_party' | 'known_external_google' | 'external_unclassified' | 'opaque';

function getScriptResourceUrl(event: Event): string | undefined {
  const filename = (event as ErrorEvent).filename?.trim();
  if (filename) return filename;

  if (event.target instanceof HTMLScriptElement && event.target.src) {
    return event.target.src;
  }

  if (isScriptPreloadLink(event.target) && event.target.href) {
    return event.target.href;
  }

  return undefined;
}

function isScriptPreloadLink(target: EventTarget | null): target is HTMLLinkElement {
  if (!(target instanceof HTMLLinkElement)) return false;
  return (
    target.relList.contains('modulepreload') ||
    (target.relList.contains('preload') && target.as.toLowerCase() === 'script')
  );
}

function isScriptResourceEventTarget(target: EventTarget | null): boolean {
  if (target instanceof HTMLScriptElement) return true;
  return isScriptPreloadLink(target);
}

function getResourceTargetKind(
  event: Event
): 'script' | 'modulepreload' | 'script_preload' | 'error_event' | 'unknown' {
  // ErrorEvents describe execution failures even when dispatched on a script.
  if (event instanceof ErrorEvent) return 'error_event';
  if (event.target instanceof HTMLScriptElement) return 'script';
  if (isScriptPreloadLink(event.target)) {
    return event.target.relList.contains('modulepreload') ? 'modulepreload' : 'script_preload';
  }
  return 'unknown';
}

// Relative eval sourceURL filenames do not establish a resource's origin.
function getHttpResourceUrl(resourceUrl: string | undefined): string | undefined {
  if (!resourceUrl) return undefined;

  try {
    const resource = new URL(resourceUrl);
    return resource.protocol === 'http:' || resource.protocol === 'https:'
      ? resource.href
      : undefined;
  } catch {
    return undefined;
  }
}

function classifyScriptAttribution(resourceUrl: string | undefined): ScriptAttribution {
  if (!resourceUrl) return 'opaque';

  try {
    const resource = new URL(resourceUrl);
    if (resource.protocol !== 'http:' && resource.protocol !== 'https:') return 'opaque';
    if (resource.origin === window.location.origin) return 'first_party';

    const hostname = resource.hostname.toLowerCase();
    return KNOWN_GOOGLE_SCRIPT_HOSTS.some(
      (domain) => hostname === domain || hostname.endsWith(`.${domain}`)
    )
      ? 'known_external_google'
      : 'external_unclassified';
  } catch {
    return 'opaque';
  }
}

export function installGlobalHandlers(): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const onError = (event: ErrorEvent) => {
    if (event.target instanceof Element && !isScriptResourceEventTarget(event.target)) return;

    const resourceCandidate = getScriptResourceUrl(event);
    const resourceUrl = getHttpResourceUrl(resourceCandidate);
    const message =
      event.message || (resourceCandidate ? 'Script resource failed to load' : 'Unknown error');

    const hasOpaqueScriptError = /^Script error\.?$/i.test(message.trim());
    const scriptAttribution =
      resourceCandidate || hasOpaqueScriptError
        ? classifyScriptAttribution(resourceUrl)
        : undefined;

    reportError({
      error: event.error ?? new Error(message),
      source: 'window.onerror',
      severity: 'error',
      context: {
        ...(scriptAttribution ? { scriptAttribution } : {}),
        resourceTargetKind: getResourceTargetKind(event),
      },
      network: resourceUrl ? { url: resourceUrl } : undefined,
    });
  };

  const onSecurityPolicyViolation = (event: SecurityPolicyViolationEvent) => {
    if (!['script-src', 'script-src-elem', 'script-src-attr'].includes(event.effectiveDirective))
      return;
    if (event.disposition !== 'enforce' && event.disposition !== 'report') return;
    const resourceUrl = getHttpResourceUrl(event.blockedURI);
    reportError({
      error: new Error(
        event.disposition === 'enforce'
          ? 'Script blocked by Content Security Policy'
          : 'Script would be blocked by report-only Content Security Policy'
      ),
      source: 'window.securitypolicyviolation',
      severity: event.disposition === 'enforce' ? 'error' : 'warning',
      context: {
        action: `csp_${event.disposition}:${event.effectiveDirective}`,
        scriptAttribution: classifyScriptAttribution(resourceUrl),
      },
      network: resourceUrl ? { url: resourceUrl } : undefined,
    });
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;

    reportError({
      error:
        reason instanceof Error
          ? reason
          : new Error(typeof reason === 'string' ? reason : 'Unhandled rejection'),
      source: 'window.unhandledrejection',
      severity: 'error',
    });
  };

  window.addEventListener('error', onError, true);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
  window.addEventListener('securitypolicyviolation', onSecurityPolicyViolation);

  return () => {
    window.removeEventListener('error', onError, true);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
    window.removeEventListener('securitypolicyviolation', onSecurityPolicyViolation);
  };
}
